import moment from 'moment-timezone';
import { GLOWTOUCH_ACCOUNT_ID } from '@ubeya/shared/utils/accounts';
import { STATUS } from '@ubeya/shared-web/constants';
import { LIVE_VIEW_CLOCK_IN_GRACE_MINUTES } from 'liveView/CellRenderers/LiveView/constants';
import { toMomentWithTz } from 'liveView/utils';

// Overtime grace after the scheduled end before a clock-out counts as a late clock-out.
const LATE_CLOCK_OUT_GRACE_MINUTES = 30;

// Per-timesheet flags the Live View tab tally (getLiveViewAttendanceStats) sums up.
export const LIVE_ATTENDANCE_FLAG_KEYS = [
  'absent',
  'notStarted',
  'lateAttendance',
  'checkedIn',
  'checkedOut',
  'clockedIn',
  'clockedOut',
  'onTimeClockIn',
  'lateClockIn',
  'earlyClockOut',
  'lateClockOut'
];

// Derived facts the standalone Live View route (getStatusesFromFlags → STATUS[]) needs but the tab
// tally does not. Kept out of LIVE_ATTENDANCE_FLAG_KEYS so adding them can't change the tab's counts.
const LIVE_STATUS_FLAG_KEYS = ['inShift', 'onBreak', 'undertime', 'finished'];

const EMPTY_FLAGS = [...LIVE_ATTENDANCE_FLAG_KEYS, ...LIVE_STATUS_FLAG_KEYS].reduce(
  (output, key) => ({ ...output, [key]: false }),
  {}
);

// Hours after the shift start before a not-clocked-in employee counts as absent
export const getAbsentAfterHours = (accountId) => (accountId === GLOWTOUCH_ACCOUNT_ID ? 4.5 : 6);

export const getAttendanceNow = (timezone) => (timezone ? moment().tz(timezone) : moment());

export const getAttendanceFlags = ({
  startTime,
  endTime,
  checkInTime,
  checkOutTime,
  shiftStartTime,
  shiftEndTime,
  breakStart,
  breakEnd,
  timezone,
  accountId,
  now: providedNow
}) => {
  const flags = { ...EMPTY_FLAGS };

  const absentAfterHours = getAbsentAfterHours(accountId);
  // callers that classify several slots in one pass (e.g. the swap picker) share a single clock so
  // grace-boundary status, counts, and late labels can't disagree; otherwise fall back to a fresh clock.
  const now = providedNow || getAttendanceNow(timezone);

  // parse each timestamp once, in the project timezone; clone before add/subtract (moment mutates)
  const shiftStart = toMomentWithTz(shiftStartTime, timezone);
  const shiftEnd = toMomentWithTz(shiftEndTime, timezone);
  const actualIn = toMomentWithTz(startTime, timezone);
  const actualOut = toMomentWithTz(endTime, timezone);

  // absent short-circuits — all other statuses are irrelevant once a no-show is past the window
  if (!startTime && shiftStart && shiftStart.clone().add(absentAfterHours, 'hour').isBefore(now)) {
    flags.absent = true;
    return flags;
  }

  const graceEnd = shiftStart && shiftStart.clone().add(LIVE_VIEW_CLOCK_IN_GRACE_MINUTES, 'minutes');

  if (!startTime && !checkInTime && !checkOutTime && !endTime) {
    // no schedule to compare against → treat as not started rather than late
    if (!graceEnd || !now.isAfter(graceEnd)) {
      flags.notStarted = true;
    } else {
      flags.lateAttendance = true;
    }
  }

  // --> clock in -> check in -> check out -> clock out
  if (checkInTime && !checkOutTime && !endTime) {
    flags.checkedIn = true;
  }

  if (checkOutTime && !endTime) {
    flags.checkedOut = true;
  }

  if (startTime) {
    if (!checkInTime && !checkOutTime && !endTime) {
      flags.clockedIn = true;
    }

    if (graceEnd && actualIn && actualIn.isAfter(graceEnd)) {
      flags.lateClockIn = true;
    } else {
      flags.onTimeClockIn = true;
    }
  }

  if (endTime) {
    flags.clockedOut = true;

    if (
      actualOut &&
      shiftEnd &&
      actualOut.isBefore(shiftEnd.clone().subtract(LIVE_VIEW_CLOCK_IN_GRACE_MINUTES, 'minute'))
    ) {
      flags.earlyClockOut = true;
    }

    if (actualOut && shiftEnd && actualOut.isAfter(shiftEnd.clone().add(LATE_CLOCK_OUT_GRACE_MINUTES, 'minute'))) {
      flags.lateClockOut = true;
    }
  }

  flags.inShift = !!startTime && !endTime;
  flags.finished = !!startTime && !!endTime;
  flags.onBreak = !!breakStart && !breakEnd;
  flags.undertime = !!startTime && !!shiftEnd && !!actualOut && shiftEnd.isAfter(actualOut);

  return flags;
};

// Collapse the canonical flags into the standalone Live View route's STATUS[] (LiveSchedulingView /
// LiveViewSummary). Order + membership match the former getSlotLiveStatuses exactly: absent stands
// alone; otherwise late → in-shift → on-break → undertime → finished.
export const getStatusesFromFlags = (flags) => {
  if (flags.absent) {
    return [STATUS.ABSENT];
  }

  const statuses = [];
  if (flags.lateAttendance) statuses.push(STATUS.LATE);
  if (flags.inShift) statuses.push(STATUS.IN_SHIFT);
  if (flags.onBreak) statuses.push(STATUS.ON_BREAK);
  if (flags.undertime) statuses.push(STATUS.UNDERTIME);
  if (flags.finished) statuses.push(STATUS.FINISHED);
  return statuses;
};
